import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Three domains, three import paths — the dashboard/KPI domain goes through the
// barrel, Bexio and revenue-analytics are imported from their own modules
// because the barrel does not re-export them. A barrel import of a name that is
// not there resolves to `undefined` at runtime, not to a build error.
import {
  fetchAdminLandlordPlacedDeposits,
  fetchDashboardCostBreakdown,
  fetchDashboardCostRanking,
  sanitizeClientErrorMessage,
} from "../../../api/adminData";
import { fetchRevenueAnalytics } from "../../../api/adminData/revenueAnalytics";
import { fetchBexioCompanyInvoiced } from "../../../api/adminData/bexio";
import {
  financeRequestParams,
  overviewWindowKey,
  selectOverviewWindow,
} from "./financeSelectors";

/**
 * Every read behind the Finanzen tab, in one place.
 *
 * FOUR REQUESTS, ONE PERIOD
 * -------------------------
 * `revenue-analytics` (expected revenue + cash-in), `bexio/company-invoiced`
 * (invoiced on the issue_date axis), `dashboard/cost-breakdown` (operating
 * costs) and `dashboard/cost-ranking` (the same costs, per unit, top 5). Each is
 * a single bulk call for the selected period.
 *
 * THE RANGE IS NO LONGER REFUSED (P3c)
 * ------------------------------------
 * These four contracts used to answer for one month only, so this hook fired
 * NOTHING for a multi-month selection and the tab said the figures were not
 * defined. P3a windowed all four: they now take `year`/`month` plus an optional
 * `to_year`/`to_month` and fold the same per-month truth over the span. So a
 * range is sent as a range, in FOUR requests — never one per month, and never
 * the range-aware legacy KPI payload, which is the cost world this surface
 * exists to stop reading.
 *
 * There is still NO aggregation in this file and there must never be. Summing
 * months in the browser would be a second KPI truth beside the one the services
 * publish (`range_aggregation: "sum_of_monthly_truths"` on the wire).
 *
 * The receipts chart adds NO request of its own: `cashflow_trend` already
 * travels inside the `revenue-analytics` response, on the `payment_date` axis.
 * The whole observed history arrives in the one call the KPI cards already make,
 * and the card selects the selected months from it.
 *
 * AND ONE REQUEST THAT HAS NO MONTH AT ALL
 * ----------------------------------------
 * `deposits/landlord-placed` answers "how much capital is placed with landlords
 * right now" and takes no period — the unit carries no deposit history, so there
 * is no as-of-then figure to ask for. It therefore lives in its OWN effect,
 * keyed on nothing but `enabled`, and a month change cannot re-run it. Putting
 * it in the month effect would fire a request whose answer cannot change and
 * would imply, by refetching under a new month label, that it belongs to that
 * month.
 *
 * Open receivables and ageing are current state too, and they are NOT a fourth
 * request: `revenue-analytics` already carries them as `current_receivables`,
 * org-wide and across all rent months. That block is re-read whenever the period
 * request goes out again, which is a side effect of the request it rides in
 * rather than a period dependency of the value — the aging service is called
 * with no period at all, so there is no parameter that could narrow it.
 *
 * THAT IS ALSO THE BUG P3c FIXES. Because the whole request was withheld for a
 * multi-month selection, the receivables block went with it: "Offene
 * Forderungen" read "—" under a range, for data that was sitting there and had
 * nothing to do with the period. It is a current-state figure that was hidden by
 * a period gate.
 *
 * Tenant cash deposits are current state as well and are not read here either:
 * `AdminUebersichtPage` already loads that snapshot and owns the details modal
 * it opens, so the tab takes it as a prop. Fetching it a second time would
 * duplicate a live request and split ownership of a working flow across two
 * files.
 *
 * The tab it replaces did not work that way: it read a legacy profit payload,
 * a legacy KPI payload, a per-unit cost fan-out and a six-month chart hook that
 * fires twelve further requests. None of those are here, and none of them may
 * come back — see `companyFinanceContract.test.js`.
 *
 * INERT UNTIL THE TAB IS ACTUALLY OPEN
 * ------------------------------------
 * `AdminUebersichtPage` mounts all four panels at once, so an ungated hook
 * would fire three finance requests at every page load for a tab nobody opened.
 * Nothing happens until `enabled` is true.
 *
 * "GESAMTE ZEIT" IS REFUSED, NOT FAKED
 * ------------------------------------
 * A window is all four contracts accept, and each is bounded at 24 months; an
 * unbounded expected revenue has no window to fold over. Falling back to the
 * to-month would answer a period nobody asked for, and quietly answering with
 * the last 24 months would put a span on screen the label does not name. So the
 * window resolves to null and the period half of the tab says so.
 *
 * ONE read still goes out in that mode, because it is honest without a window:
 * `revenue-analytics` with NO period parameters at all. What is wanted from it
 * is `current_receivables`, which answers the same whichever month the server
 * resolves for itself — that is what keeps the receivables visible under
 * "Gesamte Zeit". Its FLOW figures are not rendered; the KPI grid is not shown
 * at all in that state.
 *
 * PARTIAL FAILURE IS THE NORMAL CASE
 * ----------------------------------
 * Each source keeps its own `{ data, error }`. A dead cost service costs the
 * Betriebskosten card and the contribution margin — not the whole tab.
 *
 * STALENESS
 * ---------
 * Responses are stamped with the WINDOW they were requested for and dropped if
 * that is no longer the selected one. `let cancelled` guards unmount only; it
 * does not stop a slow April–September response landing after the user moved to
 * September, because both belong to the same live component. The ref does — and
 * it names the whole window, because "Apr–Sep" and "Sep alone" would otherwise
 * share a key.
 */

const EMPTY = { data: null, error: "" };

const INITIAL = {
  revenueAnalytics: EMPTY,
  companyInvoiced: EMPTY,
  costBreakdown: EMPTY,
  costRanking: EMPTY,
};

/**
 * How many units the cost ranking asks for.
 *
 * The contract bounds `limit` at 1..50 and defaults to 5; it is passed
 * EXPLICITLY so the request says what the card shows rather than relying on a
 * default that could move. `units_ranked` in the response is the count before
 * the limit, which is how the card can tell five-of-five from five-of-forty.
 */
const COST_RANKING_LIMIT = 5;

export function useFinanceData(period, { enabled = true } = {}) {
  const periodWindow = useMemo(() => selectOverviewWindow(period), [period]);
  // A primitive, so the effect re-runs on a real period change and not on every
  // render that rebuilds an equal object. "" for "Gesamte Zeit", which is a
  // valid selection with no window rather than a missing one — `unsupported` is
  // what tells the two apart.
  const windowKey = overviewWindowKey(periodWindow);
  const unsupported = Boolean(period) && periodWindow === null;

  const [sources, setSources] = useState(INITIAL);
  // The window `sources` actually describes — "" while nothing has landed.
  // `loading` is DERIVED from it rather than stored, because a stored flag is
  // always one render late: effects run after paint, so the frame in which the
  // tab becomes active (or the month changes) would paint the previous state
  // with `loading` still false, flashing five em-dashes or a stale month before
  // the effect could correct it. Derived, the very first such render already
  // says "loading".
  const [settledKey, setSettledKey] = useState("");
  const [reloadToken, setReloadToken] = useState(0);

  // The landlord deposit snapshot, on its own axis with its own error, its own
  // settle flag and its own retry. Nothing about it is keyed to the month.
  const [landlordDeposits, setLandlordDeposits] = useState(EMPTY);
  const [landlordSettled, setLandlordSettled] = useState(false);
  const [landlordReloadToken, setLandlordReloadToken] = useState(0);

  // The window the newest request belongs to. A ref, not state: an in-flight
  // promise closed over an OLDER window, and comparing its captured values with
  // themselves would always agree.
  const activeKeyRef = useRef("");

  useEffect(() => {
    if (!enabled) {
      // Nothing to show and nothing in flight — not "still loading", which
      // would leave the panel under a row of skeletons forever.
      activeKeyRef.current = "";
      setSources(INITIAL);
      setSettledKey("");
      return undefined;
    }

    let cancelled = false;
    // "Gesamte Zeit" has no window, and its own key — so leaving it and coming
    // back still counts as a period change rather than resolving against a
    // stale token.
    const requestedKey = windowKey || "all";
    activeKeyRef.current = requestedKey;
    // Nothing in hand for the request about to go out — including a retry of a
    // period that HAD settled, which must show its skeletons again rather than
    // leave the stale figures standing as if they were fresh.
    setSettledKey("");

    const settle = (promise, fallback) =>
      promise.then(
        (data) => ({ data, error: "" }),
        (e) => ({ data: null, error: sanitizeClientErrorMessage(e?.message, fallback) })
      );
    const skip = () => Promise.resolve(EMPTY);

    const params = financeRequestParams(periodWindow);

    Promise.all([
      // Issued in EVERY mode. Without a window it carries no period parameters
      // at all and is read for `current_receivables` alone — the figure that
      // used to vanish under a range because this whole request did.
      settle(
        fetchRevenueAnalytics(params || {}),
        "Umsatzdaten konnten nicht geladen werden."
      ),
      params
        ? settle(
            fetchBexioCompanyInvoiced(params),
            "Fakturierte Umsätze konnten nicht geladen werden."
          )
        : skip(),
      params
        ? settle(
            fetchDashboardCostBreakdown(params),
            "Betriebskosten konnten nicht geladen werden."
          )
        : skip(),
      // F2c. A FOURTH member of the same bulk call, not a follow-up: it answers
      // for the same period as the other three, and a request fired after they
      // resolve would paint the ranking one round-trip late for no reason. One
      // call for the whole list — never one per unit and never one per month.
      params
        ? settle(
            fetchDashboardCostRanking({ ...params, limit: COST_RANKING_LIMIT }),
            "Kostenranking konnte nicht geladen werden."
          )
        : skip(),
    ]).then(([revenueAnalytics, companyInvoiced, costBreakdown, costRanking]) => {
      if (cancelled) return;
      // A response for a period the user has already left is discarded, not
      // rendered: React runs cleanup for the previous period, but a slow
      // April–September request and a fast September one can still resolve out
      // of order and whichever lands LAST would win.
      if (activeKeyRef.current !== requestedKey) return;
      setSources({ revenueAnalytics, companyInvoiced, costBreakdown, costRanking });
      setSettledKey(requestedKey);
    });

    return () => {
      cancelled = true;
    };
    // `periodWindow` is derived from `windowKey`; depending on the key keeps the
    // effect from re-firing on an equal-but-new object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, windowKey, reloadToken]);

  // ── Current state: no period in the deps, so a month change leaves it alone ──
  useEffect(() => {
    if (!enabled) {
      setLandlordDeposits(EMPTY);
      setLandlordSettled(false);
      return undefined;
    }

    let cancelled = false;
    // Same reason as `settledKey` above: effects run after paint, so without
    // this a retry would leave the previous figure standing as though it were
    // the fresh one for a frame.
    setLandlordSettled(false);

    fetchAdminLandlordPlacedDeposits()
      .then(
        (data) => ({ data, error: "" }),
        (e) => ({
          data: null,
          error: sanitizeClientErrorMessage(
            e?.message,
            "Sicherheitsleistungen konnten nicht geladen werden."
          ),
        })
      )
      .then((result) => {
        if (cancelled) return;
        setLandlordDeposits(result);
        setLandlordSettled(true);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, landlordReloadToken]);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);
  const reloadLandlordDeposits = useCallback(
    () => setLandlordReloadToken((n) => n + 1),
    []
  );

  // There is a period to answer for, and what we hold is not yet its answer.
  // "Gesamte Zeit" counts: one request still goes out for the receivables, and
  // the current-state cards must show skeletons until it lands rather than
  // dashes that read as a failure.
  const requestKey = windowKey || "all";
  const loading = Boolean(enabled) && settledKey !== requestKey;

  return {
    ...sources,
    window: periodWindow,
    /** The period names no answerable window ("Gesamte Zeit"). */
    unsupported,
    loading,
    reload,
    landlordDeposits,
    // No month key to compare against — one request with no parameters, so
    // "have we got its answer yet" is the whole question.
    landlordLoading: enabled && !landlordSettled,
    reloadLandlordDeposits,
  };
}
